import { cache } from 'react'
import qs from 'qs'
import { ParsedUrlQuery } from 'querystring'

import { apiService, createRequestHeadersConfig } from 'shared/api'
import { PRODUCTS_LISTING_LIMIT } from 'shared/utils/common'

import { ListingCategoryType } from '../types/categories'
import { Brand, ProductTypes } from '../types/products'
import {
  stripHeavyProductCategoryContent,
  stripHeavyProductCategoryContentFromList
} from '../utils/categories'

const api = apiService()

interface getEntityPayload {
  token?: string
  slug?: string
  limit?: number
  distributionCenterId?: string
  query: QueryProps | ParsedUrlQuery
}

type QueryProps = {
  page?: string
  limit?: string
  category?: string
  [key: string]: unknown
}

interface GetEntityResponse {
  data: {
    data: ProductTypes | ProductTypes[]
    meta: {
      entityType: string
      total: number
      lastPage: number
      currentPage: number
    }
    productCategory: ListingCategoryType
    productsBrand: Brand
  }
}

export const getEntityBySlug = cache(
  async (payload: getEntityPayload): Promise<GetEntityResponse> => {
    const { slug = '', query = {}, token, distributionCenterId = '' } = payload
    const params = { page: 1, ...query }

    const response = (await api.get(
      `/slug/${slug}/${distributionCenterId}${qs.stringify(params, {
        addQueryPrefix: true
      })}`,
      createRequestHeadersConfig(token)
    )) as GetEntityResponse

    return {
      data: {
        ...response.data,
        data: Array.isArray(response.data.data)
          ? stripHeavyProductCategoryContentFromList(response.data.data)
          : stripHeavyProductCategoryContent(response.data.data)
      }
    }
  }
)

export const getNextEntityPage = async (
  pageParam = 1,
  data: getEntityPayload
) => {
  const { distributionCenterId, query, slug, token, limit } = data

  const responseData = await getEntityBySlug({
    token: token || '',
    slug: slug || '',
    distributionCenterId: distributionCenterId || '',
    query: {
      ...query,
      limit: limit?.toString() ?? PRODUCTS_LISTING_LIMIT.toString(),
      page: pageParam.toString()
    }
  })
  const nextProductsData = responseData.data.data as ProductTypes[]

  return nextProductsData
}

interface GetProductBySlugPayload {
  slug: string
  token?: string
  distributionCenterId?: string
}

interface GetProductBySlugResponse {
  data: {
    data: ProductTypes
  }
}

export const getProductBySlug = async (
  payload: GetProductBySlugPayload
): Promise<GetProductBySlugResponse> => {
  const { slug = '', token, distributionCenterId = '' } = payload

  const response = (await api.get(
    `/slug/${slug}/${distributionCenterId}`,
    createRequestHeadersConfig(token)
  )) as GetProductBySlugResponse

  return {
    data: {
      data: stripHeavyProductCategoryContent(response.data.data)
    }
  }
}
